'use client';

/**
 * Roster section nav — client renderer (second IA row under each roster
 * page header; roster UX audit 2026-06-10 §5).
 *
 * Renders the segment pills for the roster group that owns the current
 * pathname (group memberships in `roster-section-nav-groups.ts`). Active
 * segment = longest-prefix winner, same rule as SubPageTabs.
 *
 * Gating mirrors `NavV2SectionTabs` exactly (the full gate chain), so the
 * second row never exposes a destination the sidebar/tabs would hide:
 *   - `isHrefVisibleForProviderProgram(href, providerType, programs)` — exact
 *     accepted programme lens
 *   - `isRoleAllowedForPath(href, liveRole)` — RBAC
 *   - `isModuleNavigationVisible(href, enabledModules)` — entitlement
 *   - `isDashboardProductionSurfaceVisibleForTenant(href, ctx)` — surface
 * Organisation mode is deliberately not a presentation authority. Segments
 * with `programs` render only under the matching accepted programme lens.
 * MIXED/UNKNOWN contexts have no exact lens and fail closed.
 *
 * Server pages render this via `RosterSectionNav` (which resolves the trusted
 * programme context);
 * client pages ('use client' roster pages: leave / shift-swaps /
 * handovers / timesheets) import this component directly.
 */

import { SubPageTabs, type SubPageTab } from '@statura/ui';
import { RosterPlannerNavigationPortal } from './RosterAttentionPortal';
import { trackNavV2Event } from '@/lib/nav-v2/telemetry';
import { useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthStore } from '@/stores/auth-store';
import { useModules } from '@/contexts/module-context';
import { isRoleAllowedForPath } from '@/lib/route-access';
import { isModuleNavigationVisible } from '@/lib/module-access';
import { isDashboardProductionSurfaceVisibleForTenant } from '@/lib/production-surface-access';
import { isLaunchPrimaryNavigationVisible } from '@/lib/launch-navigation';
import { isTenantNavigationPathVisible } from '@/lib/navigation/tenant-navigation';
import { isFrontlineSelfServiceNavigationVisible } from '@/lib/navigation/frontline-self-service';
import type { ProviderProgramContext } from '@/lib/navigation/provider-type';
import { useOrgLabels } from '@/contexts/org-labels-context';
import { isHrefVisibleForProviderProgram } from '@/lib/navigation/program-route-policy';
import type { ProviderType } from '@/lib/navigation/nav-v2';
import {
  resolveRosterSectionGroup,
  type ResolvedRosterSectionGroup,
  type RosterSectionSegment,
} from './roster-section-nav-groups';
import {
  rosterSectionDispatchHref as sharedRosterSectionDispatchHref,
  type StoredDispatchWorkspace,
} from '@/lib/roster/dispatch-view-persistence';

/** Every board view belongs to Schedule. Retain URL authority and the remembered view. */
export function rosterNavigationHref(
  href: string,
  pathname: string,
  search: string,
  stored: StoredDispatchWorkspace | null,
): string {
  return sharedRosterSectionDispatchHref(href, pathname, search, stored);
}

export interface RosterSectionNavigationContext {
  providerType?: ProviderType | null;
  providerProgramContext?: ProviderProgramContext;
  liveRole?: string | null;
  enabledModules: string[] | null | undefined;
  tenantId?: string | null;
  isDemoTenant?: boolean;
  visibleGoLiveScope?: unknown;
  tenantHiddenNavigationPaths?: readonly string[] | null;
  frontlineSelfServiceOnly?: boolean;
  shiftSwapsEnabled?: boolean;
}

/** Pure, source-aligned second-row projection used by the component and tests. */
export function resolveVisibleRosterSegments(
  group: ResolvedRosterSectionGroup | null,
  context: RosterSectionNavigationContext,
): ReadonlyArray<RosterSectionSegment> {
  if (!group) return [];
  return group.segments.filter((segment) => {
    // Visibility policies match route paths; the authored view query is retained
    // only on the link, just as it is in the primary row.
    const path = segment.href.split('?')[0] ?? '';
    return (
      isHrefVisibleForProviderProgram(
        path,
        context.providerProgramContext ?? context.providerType ?? 'UNKNOWN',
        segment.programs,
        context.tenantId,
      ) &&
      // 2026-09-05 backfill of main 84e49f5ae: main's `modes` gate is not carried —
      // this branch made the provider-program lens the presentation authority
      // (mode is deliberately not one); main's shift-swaps feature gate is kept.
      (segment.featureFlag !== 'shift_swaps' || context.shiftSwapsEnabled === true) &&
      isRoleAllowedForPath(path, context.liveRole) &&
      isModuleNavigationVisible(path, context.enabledModules, {
        isDemoTenant: context.isDemoTenant,
      }) &&
      isDashboardProductionSurfaceVisibleForTenant(path, {
        tenantId: context.tenantId,
        isDemo: context.isDemoTenant,
      }) &&
      isLaunchPrimaryNavigationVisible(path, context.visibleGoLiveScope, {
        isDemoTenant: context.isDemoTenant,
      }) &&
      isTenantNavigationPathVisible(path, context.tenantHiddenNavigationPaths) &&
      isFrontlineSelfServiceNavigationVisible({
        href: path,
        role: context.liveRole,
        enabled: context.frontlineSelfServiceOnly === true,
      })
    );
  });
}

export function RosterSectionNavClient(props: {
  providerType?: ProviderType;
  embedded?: boolean;
  groups?: ReadonlyArray<SubPageTab>;
}) {
  if (!props.groups?.length) return null;
  return <RosterOwnedSectionNav {...props} />;
}

function RosterOwnedSectionNav({
  providerType,
  embedded = true,
  groups = [],
}: {
  providerType?: ProviderType;
  embedded?: boolean;
  groups?: ReadonlyArray<SubPageTab>;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const liveRole = useAuthStore((s) => s.user?.role);
  const orgLabels = useOrgLabels();
  const acceptedProviderType = providerType ?? orgLabels.providerType;
  const {
    enabledModules,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    shiftSwapsEnabled,
  } = useModules();

  const group = useMemo(
    () => resolveRosterSectionGroup(`${pathname ?? ''}?${searchParams.toString()}`),
    [pathname, searchParams],
  );

  const visibleSegments = useMemo<ReadonlyArray<RosterSectionSegment>>(() => {
    return resolveVisibleRosterSegments(group, {
      providerType: acceptedProviderType,
      providerProgramContext: orgLabels.providerProgramContext,
      liveRole,
      enabledModules,
      tenantId,
      isDemoTenant,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      frontlineSelfServiceOnly,
      shiftSwapsEnabled,
    });
  }, [
    group,
    acceptedProviderType,
    orgLabels.providerProgramContext,
    liveRole,
    enabledModules,
    tenantId,
    isDemoTenant,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    shiftSwapsEnabled,
  ]);

  // A single-segment row is navigation theatre — same rule as section-tabs.
  if (!groups || groups.length === 0) return null;

  const tabs = [...groups];
  for (const segment of visibleSegments) {
    if (!tabs.some((tab) => tab.href === segment.href)) tabs.push(segment);
  }
  const navigation = (
    <div className="w-full min-w-0" data-roster-section-nav data-embedded={embedded}>
      <SubPageTabs
        tabs={tabs}
        navigationLabel="Roster navigation"
        navigationRole="tablist"
        currentPath={
          group?.activeSegmentHref ??
          groups.find((tab) => tab.id === group?.groupId)?.href ??
          pathname ??
          ''
        }
        linkRender={({ href, ...linkProps }) => (
          <Link
            {...linkProps}
            role="tab"
            aria-selected={linkProps['aria-current'] === 'page'}
            href={rosterNavigationHref(href, pathname ?? '', searchParams.toString(), null)}
            onClick={() =>
              trackNavV2Event('nav_v2_item_clicked', { source: 'section-tabs', route: href })
            }
          />
        )}
      />
    </div>
  );
  return pathname === '/roster' || pathname === '/roster/dispatch' ? (
    <RosterPlannerNavigationPortal>{navigation}</RosterPlannerNavigationPortal>
  ) : (
    navigation
  );
}
